import { Box, SwitchApplication } from "@bcc-ui/ui-components";
import classNames from "classnames";
import { useFeature } from "flagged";
import React, { useEffect, useRef, useState } from "react";
import { Menu, MenuItem } from "react-aria-menubutton";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import style from "./navigation-menu.scss";
import AssetViewKebabMenu from "components/asset-management-system/asset-view-kebab-menu/AssetViewKebabMenu";
import { AssetIcon } from "components/icons/AssetIcon";
import { AuditLog } from "components/icons/AuditLog";
import { BmsConfiguration } from "components/icons/BmsConfiguration";
import { Dashboard } from "components/icons/Dashboard";
import { File } from "components/icons/File";
import { FileSharing } from "components/icons/FileSharing";
import { Home } from "components/icons/Home";
import { IntegrationsIcon } from "components/icons/IntegrationsIcon";
import { Learn } from "components/icons/Learn";
import { Licenses } from "components/icons/Licenses";
import { OnPremBmp } from "components/icons/OnPremBmp";
import { Reports } from "components/icons/Reports";
import { RuleIcon } from "components/icons/RuleIcon";
import { Sustainability } from "components/icons/Sustainability";
import { Tenants } from "components/icons/Tenants";
import { Users } from "components/icons/Users";
import { Workflows } from "components/icons/Workflows";
import { CreateAssetButton } from "components/navigation/CreateAssetButton";
import { NavigationMenuItem } from "components/navigation/navigation-menu-item/NavigationMenuItem";
import { NavigationTogglePanel } from "components/navigation/NavigationTogglePanel";
import {
    ASSET_MANAGEMENT_ROUTE,
    ASSET_MANAGEMENT_ROUTE_BMP,
    ASSET_OVERVIEW_ROUTE,
    ASSET_VIEWS_ROUTE,
    AUDIT_LOGS_ROUTE,
    BAM_FILE_SHARING_ROUTE,
    BAM_LICENSES_ROUTE,
    BAM_SUPPORT_ROUTE,
    BAM_USERS_ROUTE,
    BAM_WORKFLOWS_ROUTE,
    CONFIGURATION_BMS_LICENSES_ROUTE,
    DASHBOARD_ROUTE,
    FILE_SHARING_ROUTE,
    HOME_ROUTE,
    INTEGRATIONS_ROUTE,
    LICENSES_EXTRACTION_ROUTE,
    LICENSES_ROUTE,
    MCS_ROUTE,
    OUR_IMPACT_DASHBOARD_ROUTE,
    PRODUCTS_ROUTE,
    REPORTS_OUR_VIEWS_ROUTE,
    REPORTS_ROUTE,
    RouteDefinition,
    RULES_ROUTE,
    SUPPORT_ROUTE,
    SUSTAINABILITY_DASHBOARD_ROUTE,
    TENANTS_ROUTE,
    USERS_ROUTE,
    WORKFLOWS_ROUTE,
} from "components/router/Routes";
import Tooltip from "components/tooltip/Tooltip";
import { AssetCustomView } from "domain/asset-management/assets";
import { MAX_TENANT_LAYER } from "domain/globalConstants";
import { FLAG_ASSET_MANAGEMENT } from "services/feature/FeatureFlagService";
import { getAllTenantsDetails, getCurrentTenantDetails, getTenantLayer } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import { Application } from "store/application";
import { resolveColor } from "utils/commonFunctions";

import iconPrivateView from "assets/images/icons/icon_private_view.svg";

interface MainMenuItem extends Partial<RouteDefinition> {
    title: string;
    childRoutes: RouteDefinition[];
    icon: JSX.Element;
    mainRoute?: RouteDefinition;
}
export type MenuRoute = MainMenuItem | RouteDefinition;

const isMainMenuItem = (route: MenuRoute): route is MainMenuItem => "mainRoute" in route;
const isRouteDefinition = (route: MenuRoute): route is RouteDefinition => (route as RouteDefinition).path !== undefined;

const findViewByUuid = (views: AssetCustomView[], uuid: string): AssetCustomView | undefined => {
    for (const view of views) {
        if (view.uuid === uuid) {
            return view;
        }
        const found = findViewByUuid(view.childrenViews, uuid);
        if (found !== undefined) {
            return found;
        }
    }
    return undefined;
};

interface Props {
    onKebabOpenChange?: (isOpen: boolean) => void;
}

const NavigationMenu = (props: Props) => {
    const application = useSelector((state: StoreState) => state.applicationReducer.selected);
    const { t } = useTranslation();
    const currentPath = window.location.pathname + window.location.search;
    const user = useSelector((state: StoreState) => state.userReducer.user);
    const views = useSelector((state: StoreState) => state.assetViewsReducer.views);
    const assetManagementEnabled = useFeature(FLAG_ASSET_MANAGEMENT);
    const [isAssetKebabOpened, setIsKebabOpened] = useState(false);
    const [isFolderKebabOpened, setIsFolderKebabOpened] = useState(false);

    const tenantDetails = getCurrentTenantDetails();
    const tenantLayer = getTenantLayer();
    // Checking count of accessed tenant after user login and "getAlltenantDetails" will
    // return 1 even if no tenant is accessed, hence subtracting it by 1 for calculation
    const accessedTenantsCount = getAllTenantsDetails().length - 1;

    const navigationScrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const nav = navigationScrollContainerRef.current;
        if (!nav || (!isAssetKebabOpened && !isFolderKebabOpened)) {
            return;
        }
        const blockWheel = (event: WheelEvent) => {
            event.preventDefault();
        };
        const closeKebabOnScroll = () => {
            document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
        };

        nav.addEventListener("wheel", blockWheel, { passive: false });
        nav.addEventListener("scroll", closeKebabOnScroll);

        return () => {
            nav.removeEventListener("wheel", blockWheel);
            nav.removeEventListener("scroll", closeKebabOnScroll);
        };
    }, [isAssetKebabOpened, isFolderKebabOpened]);

    useEffect(() => {
        props.onKebabOpenChange?.(isAssetKebabOpened || isFolderKebabOpened);
    }, [isAssetKebabOpened, isFolderKebabOpened]);

    const getTooltip = (route: MenuRoute): JSX.Element => {
        const routes = new Map([
            [
                OUR_IMPACT_DASHBOARD_ROUTE.title,
                {
                    title: t("Routes.ourImpact.tooltip.unpaid.title"),
                    body: t("Routes.ourImpact.tooltip.unpaid.calculate"),
                    footer: t("Routes.ourImpact.tooltip.unpaid.contactBlancco"),
                },
            ],
            [
                WORKFLOWS_ROUTE.title,
                {
                    title: t("Routes.workflowsMenu.unpaidTitle"),
                    body: t("Routes.workflowsMenu.automateWorkflowsAndSimplifyCompliance"),
                    footer: t("Routes.workflowsMenu.contactBlancco"),
                },
            ],
        ]);
        const dto = routes.get(route.title);
        if (dto == null) {
            return <></>;
        }
        return (
            <div className={style.tooltip}>
                <div className={style.title}>{dto.title}</div>
                <div className={style.content}>{dto.body}</div>
                <div>{dto.footer}</div>
            </div>
        );
    };

    const hasActiveLink = (routes: RouteDefinition[]): boolean => {
        const isRouteOrChildActive = (route: RouteDefinition): boolean => {
            if (currentPath === CONFIGURATION_BMS_LICENSES_ROUTE.path) {
                return false;
            }
            if (route.path === currentPath) {
                return true;
            }

            return route.childRoutes.some((childRoute) => isRouteOrChildActive(childRoute));
        };

        return routes.some((route) => isRouteOrChildActive(route));
    };

    const getIconColor = (route: RouteDefinition) => {
        const isRouteActive = currentPath === route.path;
        const isChildActive = hasActiveLink(route.childRoutes);

        return isRouteActive || isChildActive
            ? resolveColor("--app-navigationPanel-iconColorActive")
            : resolveColor("--app-navigationPanel-iconColor");
    };

    const isRouteEnabled = (route: MenuRoute): boolean => {
        if (!isRouteDefinition(route)) {
            return false;
        }

        if (!route.isValidUser(user)) {
            return false;
        }

        const flag = route.getFeatureFlag && route.getFeatureFlag();
        if (flag != null && !useFeature(flag)) {
            return false;
        }

        if (
            tenantLayer + accessedTenantsCount == MAX_TENANT_LAYER &&
            tenantDetails.featureLicenses.includes("FEATURE_TENANT_MANAGEMENT")
        ) {
            tenantDetails.featureLicenses.splice(tenantDetails.featureLicenses.indexOf("FEATURE_TENANT_MANAGEMENT"), 1);
        }

        if (!route.isAccessibleTo(tenantDetails)) {
            return false;
        }

        return true;
    };

    const bmpMainMenuItems: MainMenuItem[] = [
        {
            title: HOME_ROUTE.title,
            mainRoute: HOME_ROUTE,
            childRoutes: [],
            icon: <Home color={getIconColor(HOME_ROUTE)} />,
        },
        {
            title: PRODUCTS_ROUTE.title,
            mainRoute: PRODUCTS_ROUTE,
            childRoutes: [],
            icon: <SwitchApplication size={24} color={getIconColor(PRODUCTS_ROUTE)} />,
        },
        {
            title: DASHBOARD_ROUTE.title,
            mainRoute: DASHBOARD_ROUTE,
            childRoutes: [],
            icon: <Dashboard color={getIconColor(DASHBOARD_ROUTE)} />,
        },
        {
            title: SUSTAINABILITY_DASHBOARD_ROUTE.title,
            mainRoute: SUSTAINABILITY_DASHBOARD_ROUTE,
            childRoutes: [],
            icon: <Sustainability color={getIconColor(SUSTAINABILITY_DASHBOARD_ROUTE)} />,
        },
        {
            title: REPORTS_ROUTE.title,
            mainRoute: REPORTS_ROUTE,
            childRoutes: [],
            icon: <Reports color={getIconColor(REPORTS_ROUTE)} />,
        },
        {
            title: WORKFLOWS_ROUTE.title,
            mainRoute: WORKFLOWS_ROUTE,
            childRoutes: [],
            icon: <Workflows color={getIconColor(WORKFLOWS_ROUTE)} />,
        },
        {
            title: LICENSES_ROUTE.title,
            mainRoute: LICENSES_ROUTE,
            childRoutes: [],
            icon: <Licenses color={getIconColor(LICENSES_ROUTE)} />,
        },
        {
            title: USERS_ROUTE.title,
            mainRoute: USERS_ROUTE,
            childRoutes: [],
            icon: <Users color={getIconColor(USERS_ROUTE)} />,
        },
        {
            title: TENANTS_ROUTE.title,
            mainRoute: TENANTS_ROUTE,
            childRoutes: [],
            icon: <Tenants color={getIconColor(TENANTS_ROUTE)} />,
        },
        {
            title: AUDIT_LOGS_ROUTE.title,
            mainRoute: AUDIT_LOGS_ROUTE,
            childRoutes: [],
            icon: <AuditLog color={getIconColor(AUDIT_LOGS_ROUTE)} />,
        },
        {
            title: MCS_ROUTE.title,
            mainRoute: MCS_ROUTE,
            childRoutes: [],
            icon: <OnPremBmp color={getIconColor(MCS_ROUTE)} />,
        },
        {
            title: CONFIGURATION_BMS_LICENSES_ROUTE.title,
            mainRoute: CONFIGURATION_BMS_LICENSES_ROUTE,
            childRoutes: [],
            icon: <BmsConfiguration color={getIconColor(CONFIGURATION_BMS_LICENSES_ROUTE)} />,
        },
        {
            title: FILE_SHARING_ROUTE.title,
            mainRoute: FILE_SHARING_ROUTE,
            childRoutes: [],
            icon: <FileSharing color={getIconColor(FILE_SHARING_ROUTE)} />,
        },
        {
            title: SUPPORT_ROUTE.title,
            mainRoute: SUPPORT_ROUTE,
            childRoutes: [],
            icon: <Learn color={getIconColor(SUPPORT_ROUTE)} />,
        },
    ];

    const assetOverviewMenuItem: MainMenuItem = {
        title: ASSET_MANAGEMENT_ROUTE_BMP.title,
        mainRoute: ASSET_MANAGEMENT_ROUTE_BMP,
        childRoutes: [],
        icon: <AssetIcon color={getIconColor(ASSET_MANAGEMENT_ROUTE_BMP)} />,
    };
    const workflowsIndex = bmpMainMenuItems.findIndex((item) => item.mainRoute === WORKFLOWS_ROUTE);
    const bmpWithAssetsMenuItems: MainMenuItem[] = [
        ...bmpMainMenuItems.slice(0, workflowsIndex + 1),
        assetOverviewMenuItem,
        ...bmpMainMenuItems.slice(workflowsIndex + 1),
    ];

    const bamMainMenuItems: MainMenuItem[] = [
        {
            title: ASSET_MANAGEMENT_ROUTE.title,
            mainRoute: ASSET_MANAGEMENT_ROUTE,
            childRoutes: [],
            icon: <AssetIcon color={getIconColor(ASSET_MANAGEMENT_ROUTE)} />,
        },
        {
            title: BAM_LICENSES_ROUTE.title,
            mainRoute: BAM_LICENSES_ROUTE,
            childRoutes: [],
            icon: <Licenses color={getIconColor(LICENSES_ROUTE)} />,
        },
        {
            title: BAM_USERS_ROUTE.title,
            mainRoute: BAM_USERS_ROUTE,
            childRoutes: [],
            icon: <Users color={getIconColor(USERS_ROUTE)} />,
        },
        {
            title: RULES_ROUTE.title,
            mainRoute: RULES_ROUTE,
            childRoutes: [],
            icon: <RuleIcon color={getIconColor(RULES_ROUTE)} />,
        },
        {
            title: INTEGRATIONS_ROUTE.title,
            mainRoute: INTEGRATIONS_ROUTE,
            childRoutes: [],
            icon: <IntegrationsIcon color={getIconColor(INTEGRATIONS_ROUTE)} />,
        },
        {
            title: BAM_WORKFLOWS_ROUTE.title,
            mainRoute: BAM_WORKFLOWS_ROUTE,
            childRoutes: [],
            icon: <Workflows color={getIconColor(BAM_WORKFLOWS_ROUTE)} />,
        },
        {
            title: BAM_FILE_SHARING_ROUTE.title,
            mainRoute: BAM_FILE_SHARING_ROUTE,
            childRoutes: [],
            icon: <FileSharing color={getIconColor(BAM_FILE_SHARING_ROUTE)} />,
        },
        {
            title: BAM_SUPPORT_ROUTE.title,
            mainRoute: BAM_SUPPORT_ROUTE,
            childRoutes: [],
            icon: <Learn color={getIconColor(SUPPORT_ROUTE)} />,
        },
    ];

    const getMenuItem = (item: MainMenuItem) => deduceMainMenuItems().find((menuItem) => menuItem.title === item.title);

    const createMenuItems = (route: RouteDefinition, index: number, isInsideFolder = false) => {
        const flag = route.getFeatureFlag();
        const isUsedAsMainRoute = deduceMainMenuItems().find((item) => item.mainRoute === route);

        if (flag != null && !useFeature(flag)) {
            return null;
        }

        if (!isRouteEnabled(route) && !route.advertised) {
            return null;
        }

        if (!route.isValidUser(user)) {
            return null;
        }

        // Prevent a route rendering again if it's already used as a top level link route.
        if (isUsedAsMainRoute) {
            return null;
        }

        const isDirectAssetChild = ASSET_VIEWS_ROUTE.childRoutes.some((each) => each === route);
        const isAssetFolder = isDirectAssetChild && route.canHaveChildren;
        const isAssetGroup = !route.canHaveChildren && (isDirectAssetChild || isInsideFolder);
        const isAssetViewsRoute = route === ASSET_VIEWS_ROUTE;
        const pathUuid = route.path.split("/").pop();
        const pathElement = views && pathUuid ? findViewByUuid(views, pathUuid) : undefined;

        if (isAssetViewsRoute) {
            return (
                <Box columnLayout key={route.path} className={style.customViewsSection}>
                    <Box>
                        <span className={style.customViewsSectionTitle}>{t(route.title)}</span>
                    </Box>

                    <Box columnLayout className={classNames(style.customViewsMenu, style.menu)}>
                        <div
                            ref={navigationScrollContainerRef}
                            className={classNames(style.menuItem, style.assetsMenuItem)}
                        >
                            <ul>
                                {route.childRoutes.map((childRoute, index) =>
                                    createMenuItems(childRoute, index, false)
                                )}
                            </ul>
                        </div>
                    </Box>
                    <CreateAssetButton />
                </Box>
            );
        }
        const isDeletedAssetFolder = isAssetFolder && views !== null && !pathElement;

        if (isDeletedAssetFolder) {
            return null;
        }
        if (route === REPORTS_OUR_VIEWS_ROUTE || route === LICENSES_EXTRACTION_ROUTE || isAssetFolder) {
            return (
                <li key={route.path}>
                    <NavigationTogglePanel
                        isFolderKebabOpened={setIsFolderKebabOpened}
                        isOpen={hasActiveLink(route.childRoutes)}
                        id={route.title}
                        openId={hasActiveLink(route.childRoutes) ? route.title : undefined}
                        title={t(route.title)}
                        toggleButtonTestId={route.testId}
                        className={classNames(style.customViewsMenu, { [style.folderPanel]: isAssetFolder })}
                        isAssetFolder={isAssetFolder}
                        isArchived={route.isArchived}
                        hasChildren={route.childRoutes.length > 0}
                        path={route.path}
                        panelType={route === REPORTS_OUR_VIEWS_ROUTE ? "reports" : isAssetFolder ? "assets" : undefined}
                    >
                        <Menu>
                            <Box columnLayout gap="Xs">
                                <MenuItem className={style.menuItem}>
                                    <ul>
                                        {route.childRoutes.map((childRoute, index) =>
                                            createMenuItems(childRoute, index, isAssetFolder)
                                        )}
                                    </ul>
                                </MenuItem>
                            </Box>
                        </Menu>
                    </NavigationTogglePanel>
                </li>
            );
        }

        if (route.childRoutes && route.childRoutes.length > 0 && !route.advertised) {
            return <li key={route.path}>{createMenuStructure(route, index)}</li>;
        }

        const isDisabledAndAdvertised = !isRouteEnabled(route) && (route.advertised ?? false);

        const isDeletedAssetGroup = isAssetGroup && views !== null && !pathElement;

        if (isDeletedAssetGroup) {
            return null;
        }
        return (
            <li key={route.path} data-index={index}>
                <NavigationMenuItem
                    as="link"
                    to={route.path}
                    testId={route.testId}
                    isAdvertised={isDisabledAndAdvertised}
                    tooltipContent={getTooltip(route)}
                    className={isInsideFolder || pathElement ? style.assetGroupItem : undefined}
                >
                    <>
                        <Box verticalAlignment="Center" gap="Xxs" className={style.menuItemContent}>
                            {(isInsideFolder || isAssetGroup) && (
                                <Box>
                                    <File size={20} color={resolveColor("--app-navigationPanel-iconColorAsset")} />
                                </Box>
                            )}
                            <span className={style.menuItemTruncation}>{t(route.title)}</span>
                        </Box>
                        <Box>
                            <div onClick={(e) => e.preventDefault()}>
                                {pathElement && (
                                    <div className={style.kebabWrapper}>
                                        <AssetViewKebabMenu
                                            name={route.title}
                                            viewDetails={pathElement}
                                            onViewDelete={() => window.location.replace(ASSET_OVERVIEW_ROUTE.path)}
                                            noHoverStyle
                                            kebabColor="--app-navigationPanel-kebabIconColorAsset"
                                            kebabSize={"Sm"}
                                            isKebabOpened={setIsKebabOpened}
                                        />
                                    </div>
                                )}
                            </div>
                        </Box>
                    </>

                    {route.isShared === false && (
                        <Tooltip
                            content={t("CreateReportView.privateView.title")}
                            placement="top"
                            popperOptions={{
                                strategy: "fixed",
                                modifiers: [{ name: "preventOverflow", enabled: false }],
                            }}
                        >
                            <div className={style.tooltipIcon}>
                                <img
                                    className={style.iconPrivateView}
                                    src={iconPrivateView}
                                    alt={t("AltText.privateView")}
                                />
                            </div>
                        </Tooltip>
                    )}
                </NavigationMenuItem>
            </li>
        );
    };

    function deduceMainMenuItems() {
        if (application === Application.BAM) {
            return bamMainMenuItems;
        }
        return assetManagementEnabled ? bmpWithAssetsMenuItems : bmpMainMenuItems;
    }

    const createMenuStructure = (route: MenuRoute, index?: number) => {
        const routeIcon = isMainMenuItem(route) ? route.icon : undefined;
        const currentRoute = isMainMenuItem(route) ? (getMenuItem(route)?.mainRoute ?? route) : route;
        const hasNoSubmenu = currentRoute.childRoutes.length <= 1;
        const isDisabledAndAdvertised = !isRouteEnabled(currentRoute) && (currentRoute.advertised ?? false);

        const assetViewsRoute = currentRoute.childRoutes.find((route) => route === ASSET_VIEWS_ROUTE);
        const borderedRoutes = currentRoute.childRoutes.filter((route) => route !== ASSET_VIEWS_ROUTE);

        if (!isRouteEnabled(currentRoute) && !currentRoute.advertised) {
            return;
        }

        return (
            <ul key={route.title}>
                <li>
                    <NavigationMenuItem
                        as="link"
                        to={currentRoute.path ?? ""}
                        testId={currentRoute.testId}
                        activeClassName={
                            // Handle exceptions where React Router v5's exact prop isn't exact enough.
                            // It matches paths from the start and not from the end, which causes issues when the links are grouped
                            // differently visually and URL structure wise.
                            currentRoute === DASHBOARD_ROUTE || currentRoute === LICENSES_ROUTE
                                ? classNames({
                                      ["active"]:
                                          currentPath !== SUSTAINABILITY_DASHBOARD_ROUTE.path &&
                                          currentPath !== OUR_IMPACT_DASHBOARD_ROUTE.path &&
                                          currentPath !== CONFIGURATION_BMS_LICENSES_ROUTE.path,
                                  })
                                : currentRoute === ASSET_MANAGEMENT_ROUTE || currentRoute === ASSET_MANAGEMENT_ROUTE_BMP
                                  ? classNames({ ["active"]: hasActiveLink(currentRoute.childRoutes) })
                                  : "active"
                        }
                        data-depth={index ?? 0}
                        data-submenu={!hasNoSubmenu}
                        isAdvertised={isDisabledAndAdvertised}
                        tooltipContent={getTooltip(currentRoute)}
                    >
                        {routeIcon}
                        {t(route.title)}
                    </NavigationMenuItem>

                    {!hasNoSubmenu && hasActiveLink(currentRoute.childRoutes) && (
                        <>
                            {borderedRoutes.length > 0 && (
                                <ul className={style.menu}>
                                    {borderedRoutes.map((childRoute, index) => createMenuItems(childRoute, index))}
                                </ul>
                            )}
                            {assetViewsRoute && createMenuItems(assetViewsRoute, 0)}
                        </>
                    )}
                </li>
            </ul>
        );
    };

    return (
        <nav
            className={classNames(style.navigationMenu, style.mainNavigation, {
                [style.bamNavigationMenu]: application === Application.BAM,
            })}
        >
            {deduceMainMenuItems().map((item) => createMenuStructure(item))}
        </nav>
    );
};

export default NavigationMenu;
